/**
 * Αρχείο Video: an item's card (HPress HVID, vendor manual §4.1): create/edit with the duration derived from the
 * timecodes, categories, history (§4.3). The archive's page itself is `archive.tsx` (the LTO MAM).
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, get, post, put } from '../api.js';
import { t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { fmtDateTime, hhmmss, todayIso } from '../util.js';
import { Dialog } from './rundown-dialogs.js';

export interface VideoDto {
  id: string;
  videoType: 'edited' | 'raw' | null;
  videoTypeLabel: string | null;
  tapeNo: string | null;
  itemNo: number | null;
  eventDate: string;
  tcIn: string;
  tcOut: string;
  durationS: number;
  title: string;
  summary: string | null;
  comments: string | null;
  categories: Array<{ id: string; name: string }>;
  revision: number;
  createdAt: string;
  createdById: string | null;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  canEdit: boolean;
  canDelete: boolean;
  tapeId?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  archivedAt?: string | null;
  hasProxy?: boolean;
  /** When its preview was made (seconds), to tell a new preview's picture from the one the browser kept. */
  proxyAt?: number | null;
  restoreOpen?: string | null;
}
export interface Options {
  types: Array<{ value: 'edited' | 'raw'; label: string }>;
  categories: Array<{ id: string; name: string; active: boolean }>;
}
export interface ListResult {
  limit: number;
  more: boolean;
  videos: VideoDto[];
}
interface Revision {
  revision: number;
  action: string;
  snapshot: Record<string, unknown>;
  savedAt: string;
  savedBy: string | null;
}

const TC = /^\d{2}:[0-5]\d:[0-5]\d$/;
const tcSeconds = (s: string) => {
  const m = /^(\d{2}):(\d{2}):(\d{2})$/.exec(s);
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : 0;
};

interface FormState {
  videoType: string;
  tapeNo: string;
  itemNo: string;
  eventDate: string;
  tcIn: string;
  tcOut: string;
  title: string;
  summary: string;
  comments: string;
  categoryIds: string[];
}
const toForm = (v: VideoDto | null): FormState => ({
  videoType: v?.videoType ?? 'raw',
  tapeNo: v?.tapeNo ?? '',
  itemNo: v?.itemNo === null || v?.itemNo === undefined ? '' : String(v.itemNo),
  eventDate: v?.eventDate ?? todayIso(),
  tcIn: v?.tcIn ?? '00:00:00',
  tcOut: v?.tcOut ?? '00:00:00',
  title: v?.title ?? '',
  summary: v?.summary ?? '',
  comments: v?.comments ?? '',
  categoryIds: v?.categories.map((c) => c.id) ?? [],
});

/**
 * The item's card. `extra` adds rows of the page's own at its end (the archive: the video its preview is made from);
 * `setDuration` there fills TC τέλους from a file's length while both timecodes are still 00:00:00.
 */
export function VideoDialog({ mode, video, options, onClose, onSaved, extra }: { mode: 'new' | 'edit'; video: VideoDto | null; options: Options; onClose: () => void; onSaved: (video: VideoDto) => void; extra?: (ctl: { readOnly: boolean; busy: boolean; setDuration: (ms: number) => void }) => ReactNode }) {
  const [form, setForm] = useState<FormState>(() => toForm(video));
  const [baseRevision, setBaseRevision] = useState(video?.revision ?? 0);
  const [history, setHistory] = useState<Revision[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const readOnly = mode === 'edit' && !video?.canEdit;
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tcOk = TC.test(form.tcIn) && TC.test(form.tcOut) && tcSeconds(form.tcOut) >= tcSeconds(form.tcIn);
  const duration = tcOk ? tcSeconds(form.tcOut) - tcSeconds(form.tcIn) : 0;

  useEffect(() => {
    if (video) get<{ video: VideoDto; revisions: Revision[] }>(`/api/videos/${video.id}`).then((r) => setHistory(r.revisions)).catch(() => undefined);
  }, [video]);

  const payload = () => ({
    videoType: form.videoType ? (form.videoType as 'edited' | 'raw') : null,
    tapeNo: form.tapeNo.trim() || null,
    itemNo: form.itemNo.trim() ? Number(form.itemNo) : null,
    eventDate: form.eventDate,
    tcIn: form.tcIn,
    tcOut: form.tcOut,
    title: form.title.trim(),
    summary: form.summary.trim() || null,
    comments: form.comments.trim() || null,
    categoryIds: form.categoryIds,
  });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    setBusy(true);
    setError(null);
    try {
      const r = mode === 'new' ? await post<{ video: VideoDto }>('/api/videos', payload()) : await put<{ video: VideoDto }>(`/api/videos/${video!.id}`, { ...payload(), baseRevision });
      onSaved(r.video);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.payload && typeof err.payload === 'object' && 'current' in err.payload) {
        const cur = (err.payload as { current: VideoDto }).current;
        setForm(toForm(cur));
        setBaseRevision(cur.revision);
        setError(t('videoConflict'));
      } else setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id: string) => setForm((f) => ({ ...f, categoryIds: f.categoryIds.includes(id) ? f.categoryIds.filter((x) => x !== id) : [...f.categoryIds, id] }));
  const setDuration = (ms: number) => setForm((f) => (TC.test(f.tcIn) && TC.test(f.tcOut) && tcSeconds(f.tcIn) === 0 && tcSeconds(f.tcOut) === 0 && ms >= 1000 ? { ...f, tcOut: hhmmss(Math.floor(ms / 1000)) } : f));

  return (
    <Dialog title={mode === 'new' ? t('videoNew') : t('videoEdit')} onClose={onClose} width={820}>
      <form onSubmit={save}>
        <div className="form-grid">
          <label htmlFor="fvt">{t('videoType')} :</label>
          <select id="fvt" value={form.videoType} onChange={set('videoType')} disabled={readOnly}>
            <option value="">{t('anyType')}</option>
            {options.types.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
          <label className="req" htmlFor="fed">
            {t('eventDate')} :
          </label>
          <DateInput id="fed" required value={form.eventDate} onChange={(v) => set('eventDate')({ target: { value: v } })} disabled={readOnly} />
          <label htmlFor="ftape">{t('tapeNo')} :</label>
          <input id="ftape" type="text" maxLength={10} value={form.tapeNo} onChange={set('tapeNo')} disabled={readOnly} />
          <label htmlFor="fitem">{t('itemNo')} :</label>
          <input id="fitem" type="number" min={0} value={form.itemNo} onChange={set('itemNo')} disabled={readOnly} />
          <label htmlFor="ftcin">{t('tcIn')} :</label>
          <input id="ftcin" type="text" placeholder={t('tcFormat')} pattern="\d{2}:[0-5]\d:[0-5]\d" value={form.tcIn} onChange={set('tcIn')} disabled={readOnly} />
          <label htmlFor="ftcout">{t('tcOut')} :</label>
          <div className="range">
            <input id="ftcout" type="text" placeholder={t('tcFormat')} pattern="\d{2}:[0-5]\d:[0-5]\d" value={form.tcOut} onChange={set('tcOut')} disabled={readOnly} />
            <span className="muted">
              {t('videoDuration')}: <b>{tcOk ? hhmmss(duration) : '—'}</b>
            </span>
          </div>
          <label className="req" htmlFor="fti">
            {t('title')} :
          </label>
          <input id="fti" className="wide" type="text" required maxLength={250} value={form.title} onChange={set('title')} disabled={readOnly} autoFocus />
          <label>{t('videoCategories')} :</label>
          <div className="checks wide">
            {options.categories.map((c) => (
              <label key={c.id} className={form.categoryIds.includes(c.id) ? 'on' : ''}>
                <input type="checkbox" checked={form.categoryIds.includes(c.id)} onChange={() => toggle(c.id)} disabled={readOnly} /> {c.name}
              </label>
            ))}
          </div>
          <label htmlFor="fsum">{t('videoSummary')} :</label>
          <textarea id="fsum" className="wide" rows={5} value={form.summary} onChange={set('summary')} disabled={readOnly} />
          <label htmlFor="fcom">{t('comments')} :</label>
          <input id="fcom" className="wide" type="text" maxLength={250} value={form.comments} onChange={set('comments')} disabled={readOnly} />
          {extra?.({ readOnly, busy, setDuration })}
        </div>
        {error && <div className="banner err">{error}</div>}
        <div className="toolbar">
          {video && (
            <span className="muted" style={{ marginRight: 'auto' }}>
              {t('createdBy')}: {video.createdBy ?? '—'} · {t('createdAt')}: {fmtDateTime(video.createdAt)} · {t('revision')} {video.revision}
            </span>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          {!readOnly && (
            <button type="submit" className="btn primary" disabled={busy || !form.title.trim() || !form.eventDate || !tcOk}>
              {t('save')}
            </button>
          )}
        </div>
      </form>
      {video && history.length > 0 && (
        <details className="history">
          <summary>
            {t('history')} ({history.length})
          </summary>
          <table className="grid compact">
            <thead>
              <tr>
                <th>{t('revision')}</th>
                <th>{t('action')}</th>
                <th>{t('when')}</th>
                <th>{t('by')}</th>
                <th>{t('title')}</th>
                <th>{t('tapeNo')}</th>
                <th>{t('tcOut')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.revision}>
                  <td className="num">{h.revision}</td>
                  <td>{h.action}</td>
                  <td className="num">{fmtDateTime(h.savedAt)}</td>
                  <td>{h.savedBy ?? '—'}</td>
                  <td className="title">{String(h.snapshot.title ?? '')}</td>
                  <td className="num">{String(h.snapshot.tapeNo ?? '')}</td>
                  <td className="num">{String(h.snapshot.tcOut ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Dialog>
  );
}
